import type { EpochMs } from './types.ts';

/**
 * Time-zone helpers built on Intl (no tz database dependency).
 * All functions accept any IANA zone name ('UTC', 'America/Los_Angeles', …).
 */

export interface ZonedParts {
  year: number;
  month: number; // 1..12
  day: number; // 1..31
  hour: number; // 0..23
  minute: number;
  second: number;
  millisecond: number;
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timezone: string): Intl.DateTimeFormat {
  let f = dtfCache.get(timezone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    dtfCache.set(timezone, f);
  }
  return f;
}

/** true if `timezone` is a zone name Intl understands. */
export function isValidTimeZone(timezone: string): boolean {
  try {
    partsFormatter(timezone);
    return true;
  } catch {
    return false;
  }
}

/** Wall-clock fields of instant `t` in `timezone`. */
export function zonedParts(t: EpochMs, timezone: string): ZonedParts {
  const ms = ((t % 1000) + 1000) % 1000;
  if (timezone === 'UTC' || timezone === 'Etc/UTC' || timezone === 'Z') {
    const d = new Date(t);
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
      hour: d.getUTCHours(),
      minute: d.getUTCMinutes(),
      second: d.getUTCSeconds(),
      millisecond: ms,
    };
  }
  const out: ZonedParts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0, millisecond: ms };
  for (const p of partsFormatter(timezone).formatToParts(new Date(t))) {
    switch (p.type) {
      case 'year': out.year = Number(p.value); break;
      case 'month': out.month = Number(p.value); break;
      case 'day': out.day = Number(p.value); break;
      case 'hour': out.hour = Number(p.value) % 24; break;
      case 'minute': out.minute = Number(p.value); break;
      case 'second': out.second = Number(p.value); break;
      default: break;
    }
  }
  return out;
}

/** Offset of `timezone` from UTC at instant `t`, in ms (e.g. −25 200 000 for PDT). */
export function zoneOffsetMs(t: EpochMs, timezone: string): number {
  const p = zonedParts(t, timezone);
  const wallAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, p.millisecond);
  return wallAsUtc - t;
}

/**
 * UTC instant for a wall-clock time in `timezone`. For wall times skipped by a DST
 * spring-forward the result is shifted forward by the gap; for repeated (fall-back) wall times
 * the earlier instant is returned.
 */
export function zonedWallTimeToUtc(
  wall: { year: number; month: number; day: number; hour?: number; minute?: number; second?: number; millisecond?: number },
  timezone: string,
): EpochMs {
  const guess = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour ?? 0, wall.minute ?? 0, wall.second ?? 0, wall.millisecond ?? 0);
  const o1 = zoneOffsetMs(guess, timezone);
  const t1 = guess - o1;
  const o2 = zoneOffsetMs(t1, timezone);
  if (o1 === o2) {
    // Prefer the earlier instant for ambiguous (repeated) wall times.
    const oEarlier = zoneOffsetMs(t1 - 3_600_000, timezone);
    if (oEarlier !== o1) {
      const tE = guess - oEarlier;
      if (tE < t1 && zoneOffsetMs(tE, timezone) === oEarlier) return tE;
    }
    return t1;
  }
  const t2 = guess - o2;
  if (zoneOffsetMs(t2, timezone) === o2) return t2;
  // Non-existent wall time (spring forward): move forward by the gap.
  return guess - Math.min(o1, o2);
}

/** 'YYYY-MM-DD' of instant `t` in `timezone`. */
export function localDateString(t: EpochMs, timezone: string): string {
  const p = zonedParts(t, timezone);
  return `${String(p.year).padStart(4, '0')}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** [start, end) UTC instants of the local calendar day containing `t` (23/24/25 h long around DST). */
export function localDayBounds(t: EpochMs, timezone: string): { date: string; start: EpochMs; end: EpochMs } {
  const p = zonedParts(t, timezone);
  const start = zonedWallTimeToUtc({ year: p.year, month: p.month, day: p.day }, timezone);
  const next = new Date(Date.UTC(p.year, p.month - 1, p.day + 1));
  const end = zonedWallTimeToUtc(
    { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() },
    timezone,
  );
  return { date: localDateString(t, timezone), start, end };
}
